import { HbrIcon } from '@harbor/elements-react-standalone';
import { caretDown } from '@harbor/elements/icons';
import React, { useEffect, useState } from 'react';

const variantStyle = {
  default: 'border-2 border-gray-500 rounded-lg',
  stacked: 'border-b border-gray-300',
  minimal: 'border border-gray-400 rounded bg-white'
};

interface Props {
  headerContent?: React.ReactNode;
  className?: string;
  label?: string;
  children?: React.ReactNode;
  isOpen?: boolean;
  toggleIsOpen?: (open: boolean) => void;
  variant?: 'default' | 'stacked' | 'minimal';
  interactive?: boolean;
}

const StbAccordion = ({
                        headerContent,
                        className = null,
                        children,
                        label,
                        isOpen = false,
                        interactive = true,
                        toggleIsOpen = () => {},
                        variant = 'default'
                      }: Props) => {
  const [showBody, setShowBody] = useState<boolean>(isOpen);

  useEffect(() => {
    setShowBody(isOpen);
  }, [isOpen]);

  const toggleOpen = (): void => {
    if (!interactive) return;
    const nextOpen = !showBody;
    setShowBody(nextOpen);
    toggleIsOpen(nextOpen);
  };

  return (
    <div
      className={
        className
          ? className
          : `w-640px min-w-32 bg-white ${variantStyle[variant]}`
      }
    >
      <header
        className="flex justify-between items-center px-4 cursor-pointer py-2"
        onClick={toggleOpen}
      >
        {headerContent ?? <h4 className="break-words whitespace-normal">{label ?? 'Accordion Header'}</h4>}
        <HbrIcon
          asset={caretDown}
          className={`duration-300 ${showBody ? 'rotate-180' : ''} transition-all ml-2 ${
            !interactive ? 'opacity-50 text-gray-600' : ''
          }`}
        />
      </header>

      {/*
        Open/close animates via grid-template-rows (0fr → 1fr) rather than a fixed max-height.
        The old `maxHeight: 2000px` clamp reserved only 2000px of layout height while painting
        with overflow-visible, so any section taller than 2000px (e.g. the Leader dashboards:
        KPIs + multi-chart grids + a full table) overflowed its box and the next accordion laid
        out on top of it. The grid track sizes to the natural content height, so following
        siblings always sit below and nothing is clipped in flow.
      */}
      <div
        className="grid transition-[grid-template-rows,opacity] duration-300"
        style={{
          gridTemplateRows: showBody ? '1fr' : '0fr',
          opacity: showBody ? 1 : 0,
        }}
      >
        {/* overflow-hidden keeps a collapsed accordion from leaking content; when open we let
            content overflow visibly so chart tooltips/popovers can escape the body bounds. */}
        <div className={`min-h-0 ${showBody ? 'overflow-visible' : 'overflow-hidden'}`}>
          {children ?? (
            <p className="p-4 border-t px-4 py-2 border-gray-400">Accordion Body</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StbAccordion;